import { useState, useCallback } from 'react';
import { Loader2, Eye, AlertTriangle, CheckCircle2, Trash2, FileX, ChevronDown, ChevronRight, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function ManagedInvoiceCleanupPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const canApply = preview
    && !applying
    && (preview.totals.finalised_count > 0 || preview.totals.draft_count > 0);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.managedInvoiceCleanupPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, []);

  const runApply = useCallback(async (limit) => {
    setApplying(true);
    setExpandedRows(new Set());
    try {
      const { data } = await diagnosticsAPI.managedInvoiceCleanupApply(
        limit != null ? { limit } : undefined
      );
      setResult(data);
      setPreview(null);
      if (data.credited > 0 || data.deleted > 0) {
        toast.success(
          `Done: ${data.credited} credit note(s) issued, ${data.deleted} draft(s) deleted`
        );
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} action(s) failed. Check results below.`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, []);

  const finalisedInvoices = preview?.invoiced_bookings?.filter(
    (b) => b.pennylane_status === 'finalised'
  ) || [];
  const draftInvoices = preview?.invoiced_bookings?.filter(
    (b) => b.pennylane_status === 'draft'
  ) || [];
  const errorInvoices = preview?.invoiced_bookings?.filter(
    (b) => b.pennylane_status === 'fetch_error' || b.pennylane_status === 'unknown'
  ) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileX className="h-5 w-5 text-red-500" />
          Managed Property Invoice Cleanup
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Managed properties (e.g. Augereau Suite) should never have been invoiced.
          Finalised invoices cannot be deleted; they are reversed with a credit note
          (a permanent accounting record). Drafts are deleted. The amounts are exempt
          VAT, so there is no VAT reclaim complication, but this still affects the books.
          Confirm with the accountant before Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Action buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button onClick={runPreview} disabled={previewing} variant="default">
            {previewing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>
          <Button
            variant="destructive"
            onClick={() => runApply(1)}
            disabled={!canApply}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            Apply first booking
          </Button>
          <Button
            variant="destructive"
            onClick={() => runApply()}
            disabled={applying || (!canApply && !result?.remaining)}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            Apply all{result?.remaining > 0 ? ` (${result.remaining} remaining)` : ''}
          </Button>
        </div>

        {/* Preview results */}
        {preview && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="rounded-lg border p-3 bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">Preview (read-only, no writes)</span>
              </div>

              {/* Managed properties found */}
              {preview.managed_properties?.length > 0 && (
                <div className="text-xs space-y-1">
                  <p className="font-medium text-muted-foreground">Managed properties:</p>
                  {preview.managed_properties.map((p, i) => (
                    <span key={i} className="inline-block mr-2">
                      <Badge variant="outline" className="text-[10px]">
                        {p.property_name} ({p.platform})
                      </Badge>
                    </span>
                  ))}
                </div>
              )}

              {/* Totals */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Finalised</div>
                  <div className="text-lg font-bold tabular-nums">
                    {preview.totals.finalised_count}
                  </div>
                  <div className="text-[10px] text-muted-foreground">will get credit notes</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Drafts</div>
                  <div className="text-lg font-bold tabular-nums">
                    {preview.totals.draft_count}
                  </div>
                  <div className="text-[10px] text-muted-foreground">will be deleted</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Total reversal</div>
                  <div className="text-lg font-bold tabular-nums">
                    {preview.totals.total_reversal_amount?.toFixed(2) ?? '0.00'} EUR
                  </div>
                </div>
                {preview.already_credited > 0 && (
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Already credited</div>
                    <div className="text-lg font-bold tabular-nums text-green-600">
                      {preview.already_credited}
                    </div>
                    <div className="text-[10px] text-muted-foreground">skipped</div>
                  </div>
                )}
              </div>

              {preview.note && (
                <p className="text-xs text-muted-foreground italic">{preview.note}</p>
              )}

              {preview.fetch_errors?.length > 0 && (
                <div className="text-xs text-red-600 space-y-0.5">
                  {preview.fetch_errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>

            {/* Finalised invoices table */}
            {finalisedInvoices.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Finalised invoices (will receive credit notes)
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Invoice #</th>
                        <th className="py-1 pr-3 font-medium">Date</th>
                        <th className="py-1 pr-3 font-medium">Amount</th>
                        <th className="py-1 pr-3 font-medium">Guest</th>
                        <th className="py-1 pr-3 font-medium">Property</th>
                        <th className="py-1 pr-3 font-medium">Check-in</th>
                        <th className="py-1 pr-3 font-medium">Booking ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalisedInvoices.map((inv, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1 pr-3 font-mono">
                            {inv.invoice_number || inv.pennylane_invoice_id}
                          </td>
                          <td className="py-1 pr-3">{inv.invoice_date}</td>
                          <td className="py-1 pr-3 tabular-nums font-medium">
                            {inv.invoice_amount != null
                              ? Number(inv.invoice_amount).toFixed(2)
                              : '?'}{' '}
                            {inv.currency}
                          </td>
                          <td className="py-1 pr-3">{inv.guest_name}</td>
                          <td className="py-1 pr-3">{inv.property_name}</td>
                          <td className="py-1 pr-3">{inv.check_in}</td>
                          <td className="py-1 pr-3 font-mono">{inv.booking_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Draft invoices table */}
            {draftInvoices.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-blue-500" />
                  Draft invoices (will be deleted)
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Invoice ID</th>
                        <th className="py-1 pr-3 font-medium">Date</th>
                        <th className="py-1 pr-3 font-medium">Amount</th>
                        <th className="py-1 pr-3 font-medium">Guest</th>
                        <th className="py-1 pr-3 font-medium">Property</th>
                        <th className="py-1 pr-3 font-medium">Booking ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draftInvoices.map((inv, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1 pr-3 font-mono">{inv.pennylane_invoice_id}</td>
                          <td className="py-1 pr-3">{inv.invoice_date}</td>
                          <td className="py-1 pr-3 tabular-nums">
                            {inv.invoice_amount != null
                              ? Number(inv.invoice_amount).toFixed(2)
                              : '?'}{' '}
                            {inv.currency}
                          </td>
                          <td className="py-1 pr-3">{inv.guest_name}</td>
                          <td className="py-1 pr-3">{inv.property_name}</td>
                          <td className="py-1 pr-3 font-mono">{inv.booking_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Error invoices */}
            {errorInvoices.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-red-600">
                  Fetch errors ({errorInvoices.length})
                </h4>
                <div className="text-xs space-y-1">
                  {errorInvoices.map((inv, i) => (
                    <p key={i}>
                      Booking {inv.booking_id}, invoice {inv.pennylane_invoice_id}:{' '}
                      {inv.fetch_error || 'unknown status'}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Apply results */}
        {result && (() => {
          const allFailed = result.failed > 0 && (result.credited || 0) + (result.deleted || 0) === 0;
          return (
            <div className={`rounded-lg border p-3 space-y-3 ${
              allFailed
                ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
                : 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
            }`}>
              <div className="flex items-center gap-2">
                {allFailed ? (
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
                <span className={`text-sm font-medium ${
                  allFailed
                    ? 'text-red-800 dark:text-red-300'
                    : 'text-green-800 dark:text-green-300'
                }`}>
                  {allFailed
                    ? `Cleanup failed, ${result.failed} error(s)`
                    : 'Cleanup applied'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Credit notes issued</div>
                  <div className="text-lg font-bold tabular-nums">{result.credited}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Drafts deleted</div>
                  <div className="text-lg font-bold tabular-nums">{result.deleted}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Failed</div>
                  <div className={`text-lg font-bold tabular-nums ${result.failed > 0 ? 'text-red-600' : ''}`}>
                    {result.failed}
                  </div>
                </div>
              </div>

              {/* Per-booking results */}
              {result.results?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium w-5"></th>
                        <th className="py-1 pr-3 font-medium">Booking</th>
                        <th className="py-1 pr-3 font-medium">Property</th>
                        <th className="py-1 pr-3 font-medium">Action</th>
                        <th className="py-1 pr-3 font-medium">Result</th>
                        <th className="py-1 pr-3 font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.results.map((r, i) => {
                        const isExpanded = expandedRows.has(i);
                        const hasDetail = !r.success && (r.request_payload || r.response_body);
                        return (
                          <>
                            <tr
                              key={i}
                              className={`border-b border-border/50 ${hasDetail ? 'cursor-pointer hover:bg-muted/30' : ''}`}
                              onClick={() => {
                                if (!hasDetail) return;
                                setExpandedRows(prev => {
                                  const next = new Set(prev);
                                  if (next.has(i)) next.delete(i);
                                  else next.add(i);
                                  return next;
                                });
                              }}
                            >
                              <td className="py-1 pr-1">
                                {hasDetail && (
                                  isExpanded
                                    ? <ChevronDown className="h-3 w-3 text-muted-foreground" />
                                    : <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                )}
                              </td>
                              <td className="py-1 pr-3 font-mono">{r.booking_id}</td>
                              <td className="py-1 pr-3">{r.property_name}</td>
                              <td className="py-1 pr-3">
                                <Badge
                                  variant={r.action === 'credit_note' ? 'destructive' : 'outline'}
                                  className="text-[10px]"
                                >
                                  {r.action}
                                </Badge>
                              </td>
                              <td className="py-1 pr-3">
                                {r.success ? (
                                  <CheckCircle2 className="h-3 w-3 text-green-600 inline" />
                                ) : (
                                  <AlertTriangle className="h-3 w-3 text-red-500 inline" />
                                )}
                              </td>
                              <td className="py-1 pr-3 max-w-[300px] truncate">
                                {r.success
                                  ? (r.credit_note_number || r.credit_note_id || 'deleted')
                                  : r.error}
                              </td>
                            </tr>
                            {isExpanded && hasDetail && (
                              <tr key={`${i}-detail`} className="border-b border-border/50">
                                <td colSpan={6} className="p-3 bg-muted/20">
                                  <div className="space-y-3">
                                    {r.request_url && (
                                      <div>
                                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                          Outgoing Request ({r.request_url})
                                        </div>
                                        <pre className="text-[10px] bg-muted/50 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">
                                          {JSON.stringify(r.request_payload, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                    {r.response_body && (
                                      <div>
                                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                          Pennylane Response (HTTP {r.response_status})
                                        </div>
                                        <pre className="text-[10px] bg-red-50 dark:bg-red-950/20 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">
                                          {typeof r.response_body === 'string'
                                            ? r.response_body
                                            : JSON.stringify(r.response_body, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}
